import {Transition} from '@headlessui/react';
import React from 'react';
import BreadcrumbNav from "components/BreadcrumbNav";
import PageMeta from "components/PageMeta";
import PageMetaItem from "components/PageMetaItem";
import {Icon} from "types/Icon";
import {NgageSpinner} from "../NgageSpinner";

// Types ----

export type PageMetaEntry = {
  icon: Icon
  value: string | number
  url?: string
}

type PageProps = {
  title: string
  subTitle?: string
  meta?: Array<PageMetaEntry>
  actions?: React.ReactNode
  breadcrumbs?: Record<string, string>
  isLoading?: boolean
  children?: React.ReactNode
}

// Component ----

function Page({actions, breadcrumbs, children, isLoading, meta, title, subTitle}: PageProps) {
  return (
    <Transition show={true} appear={true}>
      <div className="max-w-[100rem] mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8 overflow-x-clip">
        <Transition.Child
          enter="transition duration-75"
          enterFrom="opacity-0 -translate-y-2"
          enterTo="opacity-100 translate-y-0"
        >
          {/* Header */}
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              {/* Breadcrumbs */}
              {breadcrumbs &&
                <BreadcrumbNav>
                  {Object.entries(breadcrumbs).map(([name, path], index) => ({name, path, isFirst: index === 0}))}
                </BreadcrumbNav>
              }

              {/* Title */}
              <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-medium leading-7 text-gray-900 sm:text-3xl sm:truncate line">{title}</h1>
                {subTitle && <p className='text-sm font-medium text-gray-500'>{subTitle}</p>}
              </div>

              {/* Meta */}
              {meta &&
                <PageMeta>
                  {Object.entries(meta).map(([, data], index) => (<PageMetaItem key={index} {...data} />))}
                </PageMeta>
              }
            </div>
            <div className="mt-4 flex space-x-2 md:mt-0 md:ml-4">
              {actions}
            </div>
          </div>
        </Transition.Child>

        {/* Body */}
        <Transition.Child
          enter="transition duration-75"
          enterFrom="opacity-0 scale-95 translate-y-5"
          enterTo="opacity-100 scale-100 translate-y-0">
          {isLoading ?
            <div className="flex mt-64 sm:mt-80 items-center justify-center">
              <NgageSpinner size={NgageSpinner.sizes.LARGE} color={NgageSpinner.colors.BRAND}/>
            </div>
            :
            <div className="mt-10">
              {children}
            </div>
          }
        </Transition.Child>

      </div>
    </Transition>
  );
}

export default Page;